import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, Box, Button } from "@mui/material";

import { getCurrentUserRoles, isViewer } from "../../utils/authorization";
import type { z } from "zod";
import { formDataToStringRecord } from "../../lib/validation/formData";
import { drawerButtonCancelProps, drawerButtonSaveProps } from "../../lib/ui/pageButtons";
import RightDrawer, { type RightDrawerWidth } from "../RightDrawer";

export type GenericDialogProps = {
  submitAction?: (formJson: Record<string, string>) => void | boolean | Promise<void | boolean>;
  body: (item: unknown) => React.ReactNode;
  maxWidth?: RightDrawerWidth | false;
  fullWidth?: boolean;
  /** Optional Zod schema; runs on raw string map from FormData before `submitAction`. */
  validationSchema?: z.ZodType<Record<string, unknown>>;
};

/** Keeps hook-based dialog bodies out of the parent dialog component tree. */
export const DialogBodyMount = ({
  body,
  item,
}: {
  body: (item: unknown) => React.ReactNode;
  item: unknown;
}) => <>{body(item)}</>;

const GenericDialog = React.forwardRef<
  {
    showDialog: (opts: Record<string, unknown>, item?: unknown, options?: { onClose?: () => void }) => void;
    closeDialog: () => void;
  },
  GenericDialogProps
>((props, ref) => {
  const { submitAction, body, maxWidth = "sm", validationSchema } = props;
  const { t } = useTranslation();
  const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | null>(null);
  const [item, setItem] = useState<unknown>(null);
  const [open, setOpen] = useState(false);
  const [viewerRole, setViewerRole] = useState(false);
  const [zodError, setZodError] = useState<string | null>(null);

  const [dialogOptions, setDialogOptions] = useState({
    submitButtonText: "",
    cancelButtonText: "",
    title: "",
    content: "",
  });

  const close = () => {
    setOpen(false);
    setZodError(null);
    onCloseCallback?.();
  };

  React.useImperativeHandle(ref, () => ({
    showDialog(opts: Record<string, unknown>, nextItem?: unknown, options: { onClose?: () => void } = {}) {
      if (options.onClose) {
        setOnCloseCallback(() => options.onClose);
      } else {
        setOnCloseCallback(null);
      }
      setItem(nextItem ?? null);
      setDialogOptions({
        submitButtonText: t("common.submit"),
        cancelButtonText: t("common.cancel"),
        title: t("common.genericDialog"),
        content: t("common.genericDialogContent"),
        ...(opts as object),
      } as typeof dialogOptions);
      setZodError(null);
      setOpen(true);
    },
    closeDialog: close,
  }));

  React.useEffect(() => {
    getCurrentUserRoles().then((roles) => setViewerRole(isViewer(roles)));
  }, []);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (viewerRole) {
      close();
      return;
    }
    if (submitAction) {
      const formData = new FormData(event.currentTarget);
      const formJson = formDataToStringRecord(formData);

      if (validationSchema) {
        const parsed = validationSchema.safeParse(formJson);
        if (!parsed.success) {
          const first = parsed.error.issues[0];
          setZodError(first?.message || t("common.validationFailed"));
          return;
        }
      }

      try {
        const shouldClose = await submitAction(formJson);
        if (shouldClose !== false) {
          close();
        }
      } catch (e) {
        console.error(e);
        setZodError(e instanceof Error ? e.message : t("common.requestFailed"));
      }
      return;
    }
    close();
  };

  return (
    <RightDrawer
      open={open}
      onClose={close}
      title={dialogOptions.title}
      subtitle={dialogOptions.content}
      width={maxWidth}
      component="form"
      componentProps={{ onSubmit, noValidate: true }}
      footer={
        <>
          <Button {...drawerButtonCancelProps} type="button" onClick={close}>
            {dialogOptions.cancelButtonText}
          </Button>
          <Button {...drawerButtonSaveProps} type="submit" disabled={viewerRole}>
            {dialogOptions.submitButtonText}
          </Button>
        </>
      }
    >
      {zodError && (
        <Box mb={1.5}>
          <Alert severity="error" onClose={() => setZodError(null)} variant="outlined" sx={{ py: 0 }}>
            {zodError}
          </Alert>
        </Box>
      )}
      {open ? (
        <DialogBodyMount
          key={
            item && typeof item === "object"
              ? [
                  String((item as { id?: unknown }).id ?? ""),
                  String((item as { category?: unknown }).category ?? ""),
                  String((item as { subCategory?: unknown }).subCategory ?? ""),
                  String((item as { fromAnomalyGuide?: unknown }).fromAnomalyGuide ?? ""),
                ].join("|")
              : "empty"
          }
          body={body}
          item={item}
        />
      ) : null}
    </RightDrawer>
  );
});

GenericDialog.displayName = "GenericDialog";

export { default as RhfFormDialog } from "./RhfFormDialog";
export type { RhfFormDialogProps } from "./RhfFormDialog";
export default GenericDialog;
